import React, { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Button, ButtonBase, SxProps, Theme, Typography } from '@mui/material';

import { GUIDE_TEXT_MAX_WIDTH } from './guidePage.constants';

export type GuideMythQuizAnswer = 'myth' | 'real';

export type GuideMythQuizQuestion = Readonly<{
  statement: string;
  answer: GuideMythQuizAnswer;
  feedback: string;
}>;

export type GuideMythQuizVerdicts = Readonly<{
  correct: string;
  incorrectMyth: string;
  incorrectReal: string;
}>;

export type GuideMythQuizTakeaway = Readonly<{
  minScore: number;
  message: string;
}>;

export type GuideMythQuizScore = Readonly<{
  label: string;
  restartLabel: string;
  takeaways: readonly GuideMythQuizTakeaway[];
}>;

export type GuideMythQuizContent = Readonly<{
  intro: string;
  mythLabel: string;
  realLabel: string;
  /** Supports `{n}` and `{total}` placeholders, e.g. "Question {n} of {total}". */
  progressLabel: string;
  nextLabel: string;
  finishLabel: string;
  verdicts: GuideMythQuizVerdicts;
  questions: readonly GuideMythQuizQuestion[];
  score: GuideMythQuizScore;
}>;

type GuideMythQuizProps = Readonly<{
  idPrefix: string;
  content: GuideMythQuizContent;
  stickerSrc: string;
  stickerAlt: string;
}>;

type ButtonState = 'idle' | 'correct' | 'wrong' | 'muted';

const takeawayFor = (score: number, takeaways: readonly GuideMythQuizTakeaway[]): string =>
  [...takeaways]
    .sort((a, b) => b.minScore - a.minScore)
    .find((takeaway) => score >= takeaway.minScore)?.message ?? '';

/** One-question-at-a-time myth/reality quiz with per-answer feedback and a final score. */
export const GuideMythQuiz: React.FC<GuideMythQuizProps> = ({
  idPrefix,
  content,
  stickerSrc,
  stickerAlt,
}) => {
  const {
    intro,
    mythLabel,
    realLabel,
    progressLabel,
    nextLabel,
    finishLabel,
    verdicts,
    questions,
    score,
  } = content;
  const total = questions.length;

  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState<GuideMythQuizAnswer | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  if (total === 0) {
    return null;
  }

  const question = questions[index];
  const answered = choice !== null;
  const isCorrect = answered && choice === question.answer;
  const isLast = index === total - 1;
  const progress = `${((index + 1) / total) * 100}%`;

  const handleAnswer = (value: GuideMythQuizAnswer) => {
    if (answered) {
      return;
    }

    setChoice(value);

    if (value === question.answer) {
      setCorrectCount((prev) => prev + 1);
    }
  };

  const handleNext = () => {
    if (isLast) {
      setFinished(true);

      return;
    }

    setIndex((prev) => prev + 1);
    setChoice(null);
  };

  const handleRestart = () => {
    setIndex(0);
    setChoice(null);
    setCorrectCount(0);
    setFinished(false);
  };

  const buttonState = (value: GuideMythQuizAnswer): ButtonState => {
    if (!answered) {
      return 'idle';
    }

    if (value === question.answer) {
      return 'correct';
    }

    return value === choice ? 'wrong' : 'muted';
  };

  const verdict = isCorrect
    ? verdicts.correct
    : question.answer === 'myth'
      ? verdicts.incorrectMyth
      : verdicts.incorrectReal;

  return (
    <Box>
      <Typography variant="text-md-regular" sx={introSx}>
        {intro}
      </Typography>

      <Box sx={cardSx}>
        <Box sx={bannerSx}>
          <Box component="img" src={stickerSrc} alt={stickerAlt} sx={stickerSx} />
        </Box>

        {finished ? (
          <Box sx={bodySx}>
            <Box sx={resultSx}>
              <Typography variant="display-md-bold" sx={scoreSx}>
                {correctCount}/{total}
              </Typography>
              <Typography variant="text-md-medium" sx={scoreLabelSx}>
                {score.label}
              </Typography>
              <Typography variant="text-md-regular" sx={takeawaySx}>
                {takeawayFor(correctCount, score.takeaways)}
              </Typography>
              <Button
                id={`${idPrefix}-restart-btn`}
                hierarchy="Primary"
                size="md"
                onClick={handleRestart}
              >
                {score.restartLabel}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={bodySx}>
            <Box sx={progressRowSx}>
              <Typography variant="text-sm-semibold" sx={progressTextSx}>
                {progressLabel.replace('{n}', String(index + 1)).replace('{total}', String(total))}
              </Typography>
              <Box sx={progressTrackSx}>
                <Box sx={progressFillSx(progress)} />
              </Box>
            </Box>

            <Box sx={dividerSx} />

            <Typography variant="text-lg-semibold" sx={statementSx}>
              {`“${question.statement}”`}
            </Typography>

            <Box sx={choicesSx}>
              <ButtonBase
                id={`${idPrefix}-myth-btn`}
                sx={choiceButtonSx(buttonState('myth'))}
                onClick={() => handleAnswer('myth')}
                disabled={answered}
                aria-label={`Guess ${mythLabel} for: ${question.statement}`}
              >
                <AppIcon
                  iconName="SlashCircle01"
                  diameter="18px"
                  color={iconColor(buttonState('myth'))}
                />
                {mythLabel}
              </ButtonBase>
              <ButtonBase
                id={`${idPrefix}-real-btn`}
                sx={choiceButtonSx(buttonState('real'))}
                onClick={() => handleAnswer('real')}
                disabled={answered}
                aria-label={`Guess ${realLabel} for: ${question.statement}`}
              >
                <AppIcon
                  iconName="CheckVerified03"
                  diameter="18px"
                  color={iconColor(buttonState('real'))}
                />
                {realLabel}
              </ButtonBase>
            </Box>

            {answered && (
              <>
                <Box sx={feedbackSx(isCorrect)}>
                  <Typography variant="text-sm-semibold" sx={verdictSx(isCorrect)}>
                    {verdict}
                  </Typography>
                  <Typography variant="text-sm-regular" sx={feedbackBodySx}>
                    {question.feedback}
                  </Typography>
                </Box>
                <Box sx={nextRowSx}>
                  <Button
                    id={`${idPrefix}-next-btn`}
                    hierarchy="Primary"
                    size="md"
                    onClick={handleNext}
                  >
                    {isLast ? finishLabel : nextLabel}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const iconColor = (state: ButtonState) => {
  if (state === 'correct') {
    return 'Colors/Text/text-success-primary';
  }

  if (state === 'wrong') {
    return 'Colors/Text/text-error-primary';
  }

  return 'Colors/Text/text-secondary';
};

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  mb: backrs.spacing['spacing-3xl'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '544px',
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const bannerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '96px',
  overflow: 'hidden',
  px: backrs.spacing['spacing-3xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary-solid'],
});

const stickerSx: SxProps<Theme> = {
  height: '120px',
  width: 'auto',
  maxWidth: '100%',
};

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  p: backrs.spacing['spacing-3xl'],
});

const progressRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const progressTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});

const progressTrackSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  height: '8px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  overflow: 'hidden',
});

const progressFillSx =
  (width: string): SxProps<Theme> =>
  ({ backrs }) => ({
    width,
    height: '100%',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors['Colors/Background/bg-brand-solid'],
    transition: 'width 0.3s ease',
  });

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  my: backrs.spacing['spacing-xl'],
});

const statementSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  mb: backrs.spacing['spacing-xl'],
});

const choicesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
});

const choiceButtonSx =
  (state: ButtonState): SxProps<Theme> =>
  ({ backrs, typography }) => {
    const stateColors = {
      idle: {
        color: backrs.colors['Colors/Text/text-secondary'],
        borderColor: backrs.colors['Colors/Border/border-primary'],
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      },
      correct: {
        color: backrs.colors['Colors/Text/text-success-primary'],
        borderColor: backrs.colors['Colors/Background/bg-success-secondary'],
        backgroundColor: backrs.colors['Colors/Background/bg-success-primary'],
      },
      wrong: {
        color: backrs.colors['Colors/Text/text-error-primary'],
        borderColor: backrs.colors['Colors/Border/border-error'],
        backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
      },
      muted: {
        color: backrs.colors['Colors/Text/text-quaternary'],
        borderColor: backrs.colors['Colors/Border/border-secondary'],
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      },
    }[state];

    return {
      ...typography['text-md-semibold'],
      flex: '1 0 0',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: backrs.spacing['spacing-sm'],
      py: '10px', // Hardcoded: matches BUTTON_SIZES md padding, which sits between spacing tokens
      px: backrs.spacing['spacing-xl'],
      borderRadius: backrs.radius['radius-md'],
      border: `1px solid ${stateColors.borderColor}`,
      color: stateColors.color,
      backgroundColor: stateColors.backgroundColor,
      transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
      '&.Mui-disabled': {
        color: stateColors.color,
      },
      '&:hover': {
        borderColor:
          state === 'idle' ? backrs.colors['Colors/Border/border-brand'] : stateColors.borderColor,
        backgroundColor:
          state === 'idle'
            ? backrs.colors['Colors/Background/bg-brand-primary']
            : stateColors.backgroundColor,
        color:
          state === 'idle' ? backrs.colors['Colors/Text/text-brand-secondary'] : stateColors.color,
      },
    };
  };

const feedbackSx =
  (correct: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mt: backrs.spacing['spacing-xl'],
    p: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${
      correct
        ? backrs.colors['Colors/Background/bg-success-secondary']
        : backrs.colors['Colors/Border/border-error']
    }`,
    backgroundColor: correct
      ? backrs.colors['Colors/Background/bg-success-primary']
      : backrs.colors['Colors/Background/bg-error-primary'],
  });

const verdictSx =
  (correct: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'block',
    mb: backrs.spacing['spacing-xxs'],
    color: correct
      ? backrs.colors['Colors/Text/text-success-primary']
      : backrs.colors['Colors/Text/text-error-primary'],
  });

const feedbackBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const nextRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  mt: backrs.spacing['spacing-xl'],
});

const resultSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: backrs.spacing['spacing-sm'],
  py: backrs.spacing['spacing-xl'],
});

const scoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const scoreLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const takeawaySx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-sm'],
  mb: backrs.spacing['spacing-lg'],
  maxWidth: '420px',
  color: backrs.colors['Colors/Text/text-secondary'],
});
